import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnalyticsProvider } from '@wbk/analytics';
import { WbkConfigProvider } from '@wbk/config';
import { UiProvider } from '@wbk/ui';
import { CurrencyProvider } from '@wbk/currency';
import { CalendarProvider } from '@wbk/calendar';
import { useGetCurrencies } from '@wbk/calendar/api';
import { POSTER_1X1_FADED, POSTER_3X1_FADED } from '@/constants/fallbackLogo';
import ErrorBoundary from '@/components/error/ErrorBoundary';
import { languages } from '@/i18n/config';
import useLocalization from './localization/useLocalization';
import { gtmTracker } from '../../trackers.config';

const Providers = ({ children }: { children?: React.ReactNode }) => {
  const { locale, setLocale } = useLocalization();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const params = useParams();

  return (
    <ErrorBoundary>
      <WbkConfigProvider
        i18n={{ useTranslation, Trans, languages }}
        router={{
          locale,
          Link,
          push: navigate,
          replace: (u: string) => navigate(u, { replace: true }),
          search,
          setSearch,
          setLocale,
          params: params as Record<string, string>,
        }}
      >
        <UiProvider imgFallbacks={{ '1x1': POSTER_1X1_FADED, '3x1': POSTER_3X1_FADED }}>
          <CalendarProvider city={{ title: t('common:riyadh'), slug: 'riyadh' }}>
            <NestedProviders>{children}</NestedProviders>
          </CalendarProvider>
        </UiProvider>
      </WbkConfigProvider>
    </ErrorBoundary>
  );
};

const NestedProviders = ({ children }: { children?: React.ReactNode }) => {
  const { locale, currentCurrency } = useLocalization();
  const { data } = useGetCurrencies({ lang: locale });
  const { pathname } = useLocation();

  return (
    <CurrencyProvider
      detectedCurrency={currentCurrency}
      currencies={(data || [])?.map((c) => ({ ...c, name: c.title, shortCode: c.code }))}
    >
      <AnalyticsProvider trackers={[gtmTracker]} currentLocation={pathname}>
        {children}
      </AnalyticsProvider>
    </CurrencyProvider>
  );
};

export default Providers;
